<script setup lang="ts">
import { computed, toRef } from 'vue'
import { id as id2 } from 'random-html-id'
import { useField } from 'vee-validate'
import { uniq } from 'lodash'

/*
 label: useProp(String),
  placeholder: useProp(String, '(auswählen)'),
  placeholderValue: useProp(Number, 0),
  hasError: useProp(Boolean),
  modelValue: useProp([String, Number], 0),
  optionsValue: useProp(String, 'id'),
  optionsName: useProp(String, 'name'),
  optionDisabled: useProp(String, 'disabled'),
  onlyOptionsWithName: useProp(Boolean, false),
  empty: useProp(Boolean, false),
  options: useProp(Array),
  parseValues: useProp(Boolean),
  required: useProp(Boolean),
  disabled: useProp(Boolean)*
*/

export interface Options {
  id: number
  name: string
  disabled?: boolean
  group?: string
}

export interface Props {
  name: string
  rules?: string
  label?: string
  placeholder?: string
  id?: string
  empty?: boolean
  disabled?: boolean
  autofocus?: boolean
  required?: boolean
  options: Options[]
  optionsValue?: string
  optionsName?: string
  selectClass?: string
  optionDisabled?: string
  onlyOptionsWithName?: boolean
  placeholderValue?: number | string | null
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  label: '',
  placeholder: '(auswählen)',
  selectClass: '',
  disabled: false,
  optionsValue: 'id',
  optionsName: 'name',
  optionDisabled: 'disabled',
  rules: '',
  onlyOptionsWithName: false,
  placeholderValue: 0,
  empty: false,
  autofocus: false,
  required: false
})

const name = toRef(props, 'name')
const rules = toRef(props, 'rules')
const label = toRef(props, 'label')

const xid = id2()
const htmlId = computed(() => `select-${xid}`)

const { value, errorMessage, meta } = useField(name, rules, { label })
const hasError = computed(() => meta.touched && errorMessage.value !== undefined)
const emit = defineEmits(['input'])

const hasGroups = computed(() => {
  let groups = props.options.map(item => item.group ? item.group : null)
  if (groups.length) {
    groups = groups.filter(item => item !== null)
    if (groups.length) {
      return uniq(groups)
    }
  }
  return null
})

const filteredOptions = (group) => props.options.filter(item => item.group === group)

</script>
<template>
  <div class="border-0 m-0 p-0 bg-transparent">
    <storm-ui-label
      v-if="label"
      :label="label"
      :required="rules.includes('required')"
      :for="htmlId"
    />
    <select
      :id="htmlId"
      v-model="value"
      :disabled="disabled"
      :autofocus="autofocus"
      class="block w-full pl-l pr-5 px-2 text-base truncate rounded border-gray-300  focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50"
      :class="[selectClass, value === placeholderValue ? 'text-gray-400' : '', disabled ? 'bg-gray-50' : '', hasError ? ' focus:border-red-400  focus:ring-red-200 border-red-300' : 'border-gray-300 focus:border-blue-400  focus:ring-blue-200']"
      @input="emit('input', $event.target.value)"
    >
      <option
        v-if="placeholder"
        :value="placeholderValue"
        :disabled="!empty"
        selected
      >
        {{ placeholder }}
      </option>
      <template v-if="hasGroups">
        <optgroup
          v-for="(group,groupIndex) of hasGroups"
          :key="groupIndex"
          :label="group"
        >
          <option
            v-for="(item, index) in filteredOptions(group)"
            :key="index"
            :value="item[optionsValue]"
            class="text-sm normal-case"
            :group="item.group"
            :selected="item[optionsValue] === value"
          >
            {{ item[optionsName] }}
          </option>
        </optgroup>
      </template>

      <template
        v-for="(item, index) in options"
        v-else
        :key="index"
      >
        <template v-if="!onlyOptionsWithName || (onlyOptionsWithName && ![undefined, null, '', ' ',false].includes(item[optionsName]))">
          <option
            :disabled="item[optionDisabled]"
            :value="item[optionsValue]"
            :selected="item[optionsValue] === value"
          >
            {{ item[optionsName] }}
          </option>
        </template>
      </template>
    </select>
  </div>
</template>
